import { useCallback, useEffect, useState } from 'react'
import { getDefaults, updateDefaults } from '../../shared/api/defaults'
import type { DefaultsResponse } from '../../shared/api/defaults'

interface UseDefaultsResult {
  data: DefaultsResponse | null
  loading: boolean
  saving: boolean
  error: string | null
  setDefaultEmail: (connectionId: string | null) => Promise<void>
  setDefaultCalendar: (calendarId: string | null) => Promise<void>
  refresh: () => Promise<void>
}

export function useDefaults(): UseDefaultsResult {
  const [data, setData] = useState<DefaultsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getDefaults())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load defaults')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const setDefaultEmail = useCallback(async (connectionId: string | null) => {
    setSaving(true)
    setError(null)
    try {
      const next = await updateDefaults({ defaultEmailConnectionId: connectionId })
      setData(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [])

  const setDefaultCalendar = useCallback(async (calendarId: string | null) => {
    setSaving(true)
    setError(null)
    try {
      const next = await updateDefaults({ defaultCalendarId: calendarId })
      setData(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [])

  return { data, loading, saving, error, setDefaultEmail, setDefaultCalendar, refresh }
}
